import { z } from 'zod';
import { IsoTimestamp, SchoolId, UserId } from './common.js';
import { EffectiveDate } from './class-membership.js';
/**
 * Tenant-scoped migration of the legacy academic relationship fields
 * (AIMSIS-18812).
 *
 * `user.classroomIds` and `user.subjectIds` predate the Class Memberships and
 * Teaching Assignments modules and are still the fields every authorization
 * path reads. This contract describes the *diagnosis* an admin reads before any
 * legacy field is touched, and the record shape the apply step writes.
 *
 * Two rules shape the whole contract:
 *
 *   1. Nothing is deleted and no legacy field is rewritten. The migration only
 *      ADDS replacement records; the legacy arrays stay exactly as they are, so
 *      a partial or failed run leaves every existing reader working.
 *   2. A value that cannot be mapped unambiguously is never silently converted.
 *      It lands in the review buckets with a reason, and an admin decides.
 */
export const LegacyAcademicMigrationStatus = z.enum([
    'notStarted',
    'previewed',
    'readyToMigrate',
    'partiallyMigrated',
    'completed',
    'requiresReview',
    'rolledBack',
]);
/** Which legacy relationship a row came from, and what it targets. */
export const LegacyAcademicRecordKind = z.enum([
    'studentClassroom',
    /** A non-teacher's legacy subject value: reported, never migrated. */
    'studentSubject',
    'teacherSubject',
    'teacherClassroom',
]);
/** The legacy array a row was read from. Recorded for provenance. */
export const LegacyAcademicField = z.enum(['classroomIds', 'subjectIds']);
export const LegacyAcademicOutcome = z.enum([
    /** Writes a Class Membership on apply. */
    'migratable',
    /** A replacement record for this pair already exists — idempotent skip. */
    'alreadyMigrated',
    /** Mappable in principle, but the target module does not exist yet. */
    'requiresReview',
    /** The legacy value resolves to no master record. */
    'missingReference',
    /** The legacy value resolves to more than one master record. */
    'ambiguous',
    /** Empty, non-string, or otherwise unusable. */
    'invalid',
]);
/**
 * Why a row is not simply migratable. A closed vocabulary rather than free
 * text, so the review queue can be grouped and counted by cause.
 */
export const LegacyAcademicReason = z.enum([
    'classroomNotInMasterData',
    'classroomAmbiguous',
    'classroomArchived',
    'subjectNotInMasterData',
    'subjectAmbiguous',
    'subjectArchived',
    /** An active membership already covers this pair — a conflict to decide. */
    'activeMembershipExists',
    /**
     * A teacher row that IS written as a Teaching Assignment during apply: the
     * teacher's legacy arrays yield exactly one subject and exactly one
     * classroom, so the single (subject, classroom) pair is forced by the data
     * rather than guessed by the migration.
     */
    'teacherAssignmentMigrationReady',
    /**
     * A teacher with resolvable values on BOTH sides but more than one on at
     * least one side, so the legacy arrays do not determine which subject is
     * taught in which classroom. Writing every combination would grant
     * authorization the legacy data never expressed, so the rows are reported
     * for an admin to pair manually.
     */
    'teacherPairingAmbiguous',
    /** A teacher subject with no classroom context cannot form an assignment. */
    'teacherSubjectWithoutClassroom',
    /** A teacher classroom with no subject cannot form an assignment. */
    'teacherClassroomWithoutSubject',
    'emptyValue',
    'invalidValue',
    'userRoleNotMigratable',
]);
/**
 * One legacy value, resolved. `legacyValue` is preserved verbatim alongside the
 * resolution so the audit trail shows what was read, not just what it became.
 */
export const LegacyAcademicMigrationRecord = z.object({
    userId: UserId,
    userName: z.string().nullable(),
    role: z.string(),
    kind: LegacyAcademicRecordKind,
    legacyField: LegacyAcademicField,
    legacyValue: z.string(),
    resolvedClassroomId: z.string().nullable(),
    resolvedSubjectId: z.string().nullable(),
    outcome: LegacyAcademicOutcome,
    reason: LegacyAcademicReason.nullable(),
    /** Actionable explanation, shown to the admin in the review queue. */
    message: z.string().nullable(),
    /**
     * The date the replacement record would carry. When the legacy source has no
     * date of its own, this is the tenant migration effective date the admin
     * approved — `dateAssumed` marks that, so an assumption is never mistaken for
     * a migrated fact.
     */
    effectiveFrom: EffectiveDate,
    dateAssumed: z.boolean(),
    /** Status the replacement record would be created with. */
    proposedStatus: z.enum(['active', 'inactive']),
    /** The row that blocks this one, when the outcome is a conflict. */
    existingMembershipId: z.string().nullable(),
});
export const LegacyAcademicMigrationCounts = z.object({
    /** Users read from the tenant, before any filtering. */
    usersScanned: z.number().int().nonnegative(),
    /** Users carrying at least one non-empty legacy value. */
    usersWithLegacyData: z.number().int().nonnegative(),
    /** Users whose role has no relationship to migrate (admin, proctor). */
    usersSkippedByRole: z.number().int().nonnegative(),
    /** Rows, partitioned by outcome — the buckets always sum to `total`. */
    total: z.number().int().nonnegative(),
    migratable: z.number().int().nonnegative(),
    alreadyMigrated: z.number().int().nonnegative(),
    requiresReview: z.number().int().nonnegative(),
    missingReferences: z.number().int().nonnegative(),
    ambiguous: z.number().int().nonnegative(),
    invalid: z.number().int().nonnegative(),
    /** Replacement records already carrying this migration's provenance. */
    migrated: z.number().int().nonnegative(),
});
/**
 * A zeroed counts object, for a run that wrote nothing (a preview or a rollback).
 *
 * Exported because BOTH writers of a `kind: 'rollback'` run record need it: the
 * per-tenant route and the fleet runner. The fleet wrote its rollback record with
 * NO `counts` at all, so a document the fleet had just produced failed
 * `LegacyAcademicMigrationRun.parse` — a strict reader of the run history would
 * reject the fleet's own rollback. One definition, so the two writers cannot
 * drift.
 *
 * A function rather than a shared constant: a mutable literal handed to two
 * writers is one accidental field assignment away from corrupting both.
 */
export function emptyLegacyAcademicMigrationCounts() {
    return {
        usersScanned: 0,
        usersWithLegacyData: 0,
        usersSkippedByRole: 0,
        total: 0,
        migratable: 0,
        alreadyMigrated: 0,
        requiresReview: 0,
        missingReferences: 0,
        ambiguous: 0,
        invalid: 0,
        migrated: 0,
    };
}
/** A run as the preview reports it — enough to offer a rollback. */
export const LegacyAcademicMigrationRunSummary = z.object({
    id: z.string(),
    kind: z.enum(['preview', 'apply', 'rollback']),
    startedAt: IsoTimestamp,
    appliedCount: z.number().int().nonnegative(),
});
export const LegacyAcademicMigrationPreview = z.object({
    status: LegacyAcademicMigrationStatus,
    /** Always false — the preview never writes. Stated in the payload itself. */
    written: z.literal(false),
    generatedAt: IsoTimestamp,
    /** The tenant migration effective date this preview was computed against. */
    effectiveFrom: EffectiveDate,
    /**
     * True when the user read hit its scan ceiling, so the counts describe only
     * the portion that was read. Surfaced so a partial read is never presented as
     * a complete figure.
     */
    truncated: z.boolean(),
    counts: LegacyAcademicMigrationCounts,
    records: z.array(LegacyAcademicMigrationRecord),
    warnings: z.array(z.string()),
    /**
     * Which structure the tenant's readers currently trust
     * (`settings.academicRelationshipSource`). Surfaced because "legacy fields
     * are still authoritative" and "the migration is live" are different
     * states, and an admin must be able to tell which one they are in.
     */
    relationshipSource: z.enum(['legacy', 'classMemberships']),
    /** Most recent run, when one exists — the status is derived from it. */
    lastRun: LegacyAcademicMigrationRunSummary.nullable(),
    /**
     * Most recent APPLY run. Separate from `lastRun` because a preview records a
     * run of its own, so the latest run is usually a preview and cannot be used
     * to offer a rollback.
     */
    lastApplyRun: LegacyAcademicMigrationRunSummary.nullable(),
    /**
     * Resume point for the next page of users, when the tenant is larger than one
     * page. `null` means every user has been examined.
     */
    nextCursor: z.string().nullable(),
});
/**
 * Applying is explicitly batched. One call writes at most `batchSize` rows and
 * reports what is left, so a tenant with thousands of legacy values cannot turn
 * a single request into an unbounded write or a timeout.
 */
export const ApplyLegacyAcademicMigrationRequest = z.object({
    effectiveFrom: EffectiveDate,
    batchSize: z.number().int().min(1).max(200).optional(),
    /**
     * Resume point for a tenant larger than one page: the last user id the
     * previous call examined. Omit to start from the beginning.
     */
    afterUserId: z.string().min(1).max(128).optional(),
    /**
     * The identity of the whole apply OPERATION, not of one call.
     *
     * A tenant larger than one batch is finished by calling this endpoint
     * repeatedly, and every call in that sequence is the same run — so the caller
     * mints the id ONCE before it starts looping and sends it on every call. The
     * run document is then accumulated under that one id, which is what makes
     * Roll back able to retire every batch the operation wrote: a per-call id
     * leaves the earlier batches active behind a "Rollback complete" message.
     *
     * Optional so a caller that does not page (one request finishes the tenant)
     * need not supply one; the server mints an id when it is absent.
     */
    runId: z.string().min(1).max(128).regex(/^[^/]+$/, 'Use a value without "/".').optional(),
});
export const ApplyLegacyAcademicMigrationResponse = z.object({
    runId: z.string(),
    /** Replacement records written by this call. */
    applied: z.number().int().nonnegative(),
    /** Rows that already had a replacement — idempotent skips. */
    alreadyMigrated: z.number().int().nonnegative(),
    /** Rows that could not be written and remain for review. */
    requiresReview: z.number().int().nonnegative(),
    /** Migratable rows still unwritten when the batch bound was reached. */
    remaining: z.number().int().nonnegative(),
    done: z.boolean(),
    status: LegacyAcademicMigrationStatus,
    warnings: z.array(z.string()),
    /**
     * Resume point for a tenant larger than one page — the last user id this
     * call examined. `null` means every user has been examined.
     */
    nextCursor: z.string().nullable(),
    /**
     * True when the classroom or subject master data exceeded its read bound.
     * Machine-readable rather than inferred from `warnings` prose: a caller must
     * not have to substring-match a message to learn that re-running cannot help.
     */
    referenceTruncated: z.boolean(),
});
/**
 * Rollback is non-destructive by contract: it retires the replacement records
 * this migration created (status `inactive`) and never deletes a record or a
 * legacy field. An admin can therefore re-run the migration afterwards.
 */
export const RollbackLegacyAcademicMigrationRequest = z.object({
    runId: z.string().min(1).max(128),
});
export const RollbackLegacyAcademicMigrationResponse = z.object({
    runId: z.string(),
    /** Replacement records retired by this call. */
    reverted: z.number().int().nonnegative(),
    /** Replacement records still to retire — the rollback is batched too. */
    remaining: z.number().int().nonnegative(),
    done: z.boolean(),
    status: LegacyAcademicMigrationStatus,
});
/** A persisted migration run, for the history an admin audits. */
export const LegacyAcademicMigrationRun = z.object({
    id: z.string(),
    schoolId: SchoolId,
    kind: z.enum(['preview', 'apply', 'rollback']),
    effectiveFrom: EffectiveDate,
    status: LegacyAcademicMigrationStatus,
    startedAt: IsoTimestamp,
    actorId: z.string(),
    /** Replacement records this run wrote; 0 for a preview or a rollback. */
    appliedCount: z.number().int().nonnegative(),
    counts: LegacyAcademicMigrationCounts,
});
//# sourceMappingURL=legacy-academic-migration.js.map